// CameraShutter.ts - Camera shutter sound and flash effect utility
// ✅ FIXED: Simultaneous sound + screenshot capture with delayed flash

export class CameraShutterEffect {
  private static audioContext: AudioContext | null = null;
  private static audioBuffer: AudioBuffer | null = null;
  private static isLoading = false;
  private static loadPromise: Promise<void> | null = null;

  /**
   * Initialize audio context and load shutter sound
   */
  private static async initialize(): Promise<void> {
    if (this.audioBuffer) return;
    
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = (async () => {
      try {
        // Initialize audio context
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        }

        const response = await fetch('/sounds/CameraShutter_CtX8n_01.wav').catch(() => {
          return this.generateShutterSound();
        });

        let arrayBuffer: ArrayBuffer;
        
        if (response instanceof Response) {
          arrayBuffer = await response.arrayBuffer();
        } else {
          arrayBuffer = response;
        }

        this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        console.log('✅ Camera shutter sound loaded successfully');
      } catch (error) {
        console.warn('⚠️ Failed to load shutter sound:', error);
        await this.createFallbackSound();
      } finally {
        this.isLoading = false;
      }
    })();

    return this.loadPromise;
  }

  private static async generateShutterSound(): Promise<ArrayBuffer> {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const sampleRate = this.audioContext.sampleRate;
    const duration = 0.15;
    const numSamples = Math.floor(sampleRate * duration);
    
    const audioBuffer = this.audioContext.createBuffer(2, numSamples, sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      
      for (let i = 0; i < numSamples; i++) {
        const t = i / sampleRate;
        const envelope = Math.exp(-t * 40);
        const noise = (Math.random() * 2 - 1) * 0.3;
        const click = Math.sin(2 * Math.PI * 1000 * t) * 0.5;
        channelData[i] = (noise + click) * envelope;
      }
    }

    const wavBuffer = this.audioBufferToWav(audioBuffer);
    return wavBuffer;
  }

  private static async createFallbackSound(): Promise<void> {
    if (!this.audioContext) return;
    const arrayBuffer = await this.generateShutterSound();
    this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
  }

  private static audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1;
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const data = new Float32Array(buffer.length * numChannels);
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        data[i * numChannels + channel] = buffer.getChannelData(channel)[i];
      }
    }

    const dataLength = data.length * bytesPerSample;
    const bufferLength = 44 + dataLength;
    const arrayBuffer = new ArrayBuffer(bufferLength);
    const view = new DataView(arrayBuffer);

    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataLength, true);

    let offset = 44;
    for (let i = 0; i < data.length; i++) {
      const sample = Math.max(-1, Math.min(1, data[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }

    return arrayBuffer;
  }

  private static writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * Play camera shutter sound (non-blocking)
   */
  static async playShutterSound(muted: boolean = false): Promise<void> {
    if (muted) {
      console.log('🔇 Shutter sound muted');
      return;
    }

    try {
      await this.initialize();

      if (!this.audioContext || !this.audioBuffer) {
        console.warn('⚠️ Audio not ready');
        return;
      }

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      const source = this.audioContext.createBufferSource();
      source.buffer = this.audioBuffer;
      
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 0.5;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      source.start(0);
      console.log('🔊 Camera shutter sound played');
    } catch (error) {
      console.warn('⚠️ Failed to play shutter sound:', error);
    }
  }

  /**
   * Create flash effect overlay with DELAYED animation
   * ✅ NEW: Flash appears AFTER screenshot to prevent white overlay capture
   */
  static createFlashEffect(delayMs: number = 0): void {
    setTimeout(() => {
      const flash = document.createElement('div');
      flash.className = 'camera-flash-overlay';
      flash.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: white;
        z-index: 99999;
        pointer-events: none;
        animation: cameraFlash 0.3s ease-out;
      `;

      if (!document.querySelector('#camera-flash-styles')) {
        const style = document.createElement('style');
        style.id = 'camera-flash-styles';
        style.textContent = `
          @keyframes cameraFlash {
            0% {
              opacity: 0;
            }
            10% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(style);
      }

      document.body.appendChild(flash);

      setTimeout(() => {
        if (flash.parentNode) {
          flash.parentNode.removeChild(flash);
        }
      }, 300);

      console.log('📸 Flash effect triggered (delayed)');
    }, delayMs);
  }

  /**
   * ✅ NEW STRATEGY: Trigger sound immediately, return instantly, delay flash
   * This ensures:
   * 1. Sound plays at the same time as screenshot capture (no delay)
   * 2. Screenshot capture happens immediately (no waiting)
   * 3. Flash effect appears AFTER screenshot (prevents white overlay)
   * 
   * @param muted - If true, only show flash without sound
   * @returns Promise that resolves immediately (no waiting)
   */
  static async trigger(muted: boolean = false): Promise<void> {
    console.log('🎬 Starting shutter trigger - sound + screenshot simultaneous');
    
    // ✅ STEP 1: Play sound immediately (non-blocking)
    this.playShutterSound(muted);
    
    // ✅ STEP 2: Schedule delayed flash (appears AFTER screenshot)
    // Delay: 100ms (enough time for screenshot capture to complete)
    this.createFlashEffect(100);
    
    // ✅ STEP 3: Return immediately - don't wait!
    // Screenshot capture happens right away
    console.log('✅ Shutter triggered - proceeding with screenshot immediately');
  }
}

export default CameraShutterEffect;